import { Loader2, Plus } from "lucide-react";
import { useMemo, type FC } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { STAGES, stageOf, type Stage } from "../model";
import type { NewRequestAction } from "../types";
import { parseSubmissionId } from "../types";
import { NewRequestMenu } from "./NewRequestMenu";

/** Waiting on the attorney first, then out with the client, then drafts. */
const NAV_ORDER: Stage[] = [
  "awaiting_review",
  "with_client",
  "drafts",
  "done",
  "expired"
];

const STAGE_LABEL = new Map(STAGES.map((s) => [s.id, s.label]));

type Props = {
  onNewRequest: (action: NewRequestAction) => void;
  busy?: boolean;
};

/**
 * The Requests row's children in the client nav: New Request first, set apart as an action, then one row per stage with its
 * count, filtering the Requests list (`?stage=`). The requests themselves are
 * in the list.
 */
export const RequestsStageNav: FC<Props> = ({ onNewRequest, busy }) => {
  const { activeArtifact, listStage, openList } = useRequestsIde();
  const { intakes } = useRequestsData();

  const counts = useMemo(() => {
    const out = new Map<Stage, number>();
    for (const intake of intakes) {
      const stage = stageOf(intake.status);
      out.set(stage, (out.get(stage) ?? 0) + 1);
    }
    return out;
  }, [intakes]);

  const openStage = useMemo<Stage | null>(() => {
    if (!activeArtifact) return null;
    const intakeId =
      activeArtifact.kind === "submission"
        ? parseSubmissionId(activeArtifact.id)?.intakeId
        : activeArtifact.id;
    const intake = intakes.find((i) => i.outgoing_intake_id === intakeId);
    return intake ? stageOf(intake.status) : null;
  }, [activeArtifact, intakes]);

  const current = activeArtifact ? openStage : listStage;

  return (
    <SidebarMenuSub
      className="mx-0 border-l-0 pr-0 pl-4"
      data-testid="requests-stage-nav"
    >
      <SidebarMenuSubItem className="mb-1 border-b border-line-1 pb-1.5">
        <NewRequestMenu
          align="start"
          onNewRequest={onNewRequest}
          trigger={
            <SidebarMenuSubButton asChild>
              <button
                type="button"
                className="h-8 w-full bg-primary/10 font-medium text-primary hover:bg-primary/15 hover:text-primary active:bg-primary/15 aria-expanded:bg-primary/15 [&>svg]:text-primary"
                disabled={busy}
                data-testid="requests-stage-new"
              >
                {busy ? <Loader2 className="animate-spin" /> : <Plus />}
                <span className="min-w-0 flex-1 truncate text-left">
                  New Request
                </span>
              </button>
            </SidebarMenuSubButton>
          }
        />
      </SidebarMenuSubItem>
      {NAV_ORDER.map((stage) => {
        const count = counts.get(stage) ?? 0;
        const label = STAGE_LABEL.get(stage) ?? stage;
        const active = current === stage;
        const attention = stage === "awaiting_review" && count > 0;
        return (
          <SidebarMenuSubItem key={stage}>
            <SidebarMenuSubButton asChild isActive={active}>
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                aria-label={`${label}, ${count} ${
                  count === 1 ? "request" : "requests"
                }`}
                onClick={() => openList({ stage })}
                className="w-full"
                data-testid={`requests-stage-${stage}`}
              >
                <span className="min-w-0 flex-1 truncate text-left">
                  {label}
                </span>
                {count > 0 ? (
                  <span
                    className={cn(
                      "ml-auto tabular-nums",
                      attention
                        ? "rounded-full bg-danger px-1.5 text-xs font-medium text-white"
                        : "text-sm text-fg-3"
                    )}
                    data-attention={attention ? "" : undefined}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                ) : null}
              </button>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
};
